import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navigation from './components/Navigation';
import HomePage from './pages/HomePage';
import BoardPage from './pages/BoardPage';
import ProfilePage from './pages/ProfilePage';
import RealtimePage from './pages/RealtimePage';
import PressedPage from './pages/PressedPage';
import ThemePage from './pages/ThemePage';
import FavoritesPage from './pages/FavoritesPage';
import AlgorithmPressedPage from './pages/AlgorithmPressedPage';
import AlgorithmThemeExecutor from './pages/AlgorithmThemeExecutor';
import AlgorithmThemeCalendar from './pages/AlgorithmThemeCalendar';
import AlgorithmBacktesting from './pages/AlgorithmBacktesting';
import StockReportPage from './pages/StockReportPage';
import NaverFinancePage from './pages/NaverFinancePage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import MenuManagement from './pages/MenuManagement';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import NewsAlertPage from './pages/NewsAlertPage';
import StudyFeedPage from './pages/StudyFeedPage';
import StudyFormPage from './pages/StudyFormPage';
import StudyDetailPage from './pages/StudyDetailPage';
import StudyEntryFormPage from './pages/StudyEntryFormPage';
import TemplateListPage from './pages/TemplateListPage';
import TemplateFormPage from './pages/TemplateFormPage';
import CrawlingDashboard from './pages/CrawlingDashboard';
import CrawlingRequestForm from './pages/CrawlingRequestForm';
import { ServerConfigProvider, useServerConfig } from './context/ServerConfigContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { JWTAuthProvider } from './context/JWTAuthContext';
import { FavoriteProvider } from './contexts/FavoriteContext';
import { hasMenuPermission, getUserGradeString } from './utils/menuPermissions';

// 메뉴 권한 기반 라우트 보호 컴포넌트
function ProtectedRoute({ children, menuId }) {
  const { user, isAuthenticated, userGrade } = useAuth();
  const { config, isConfigLoaded } = useServerConfig();

  // 설정이 로드되지 않았으면 로딩 표시
  if (!isConfigLoaded) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner"></div>
        <p>권한을 확인하는 중...</p>
      </div>
    );
  }

  // 현재 사용자의 등급 확인 (비로그인 시 guest)
  const currentUserGrade = isAuthenticated ? userGrade : null;

  // 메뉴 접근 권한 확인
  const hasPermission = hasMenuPermission(currentUserGrade, menuId, config, user);

  console.log(`🔐 [ProtectedRoute] 권한 확인: ${getUserGradeString(currentUserGrade)} -> ${menuId} = ${hasPermission}`);

  if (!hasPermission) {
    // 권한이 없으면 404처럼 처리 (board 페이지로 리다이렉트)
    return <Navigate to="/board" replace />;
  }

  return children;
}

// 관리자 전용 라우트 보호 컴포넌트
function AdminRoute({ children }) {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'moderator';

  // production 모드에서는 관리자 페이지 접근 차단
  if (process.env.NODE_ENV === 'production') {
    return <Navigate to="/board" replace />;
  }

  if (!isAuthenticated || !isAdmin) {
    // 권한이 없으면 board 페이지로 리다이렉트 (404처럼 처리)
    return <Navigate to="/board" replace />;
  }

  return children;
}

// 메인 앱 컴포넌트
function MainApp() {
  const { user, isAuthenticated, isLoading } = useAuth();

  // 관리자 권한 체크
  const isAdmin = user?.role === 'admin' || user?.role === 'moderator';

  // 인증 로딩 중
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner"></div>
        <p>앱을 준비하는 중...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <Navigation showSettings={isAuthenticated && isAdmin} />
      <Routes>
          {/* 홈 페이지 - board로 리다이렉트 */}
          <Route path="/" element={<Navigate to="/board" replace />} />
          <Route path="/home" element={<HomePage />} />

          {/* 거래 관련 페이지 - 권한 보호 */}
          <Route path="/realtime" element={
            <ProtectedRoute menuId="trading">
              <RealtimePage />
            </ProtectedRoute>
          } />
          <Route path="/pressed" element={
            <ProtectedRoute menuId="trading">
              <PressedPage />
            </ProtectedRoute>
          } />
          <Route path="/theme" element={
            <ProtectedRoute menuId="trading">
              <ThemePage />
            </ProtectedRoute>
          } />
          <Route path="/favorites" element={
            <ProtectedRoute menuId="trading">
              <FavoritesPage />
            </ProtectedRoute>
          } />

          {/* 알고리즘 관련 페이지 - 권한 보호 */}
          <Route path="/algorithm-pressed" element={
            <ProtectedRoute menuId="algorithm">
              <AlgorithmPressedPage />
            </ProtectedRoute>
          } />
          <Route path="/algorithm-theme" element={
            <ProtectedRoute menuId="algorithm">
              <AlgorithmThemeExecutor />
            </ProtectedRoute>
          } />
          <Route path="/algorithm-theme-calendar" element={
            <ProtectedRoute menuId="algorithm">
              <AlgorithmThemeCalendar />
            </ProtectedRoute>
          } />
          <Route path="/algorithm-backtesting" element={
            <ProtectedRoute menuId="algorithm">
              <AlgorithmBacktesting />
            </ProtectedRoute>
          } />

          {/* 주식 정보 - 권한 보호 */}
          <Route path="/stock-report" element={
            <ProtectedRoute menuId="stock-info">
              <StockReportPage />
            </ProtectedRoute>
          } />

          <Route path="/naver-finance" element={
            <ProtectedRoute menuId="stock-info">
              <NaverFinancePage />
            </ProtectedRoute>
          } />

          {/* 게시판 - 권한 보호 */}
          <Route path="/board" element={
            <ProtectedRoute menuId="board">
              <BoardPage />
            </ProtectedRoute>
          } />
          <Route path="/board/:boardId" element={
            <ProtectedRoute menuId="board">
              <BoardPage />
            </ProtectedRoute>
          } />
          <Route path="/board/:boardId/post/:postId" element={
            <ProtectedRoute menuId="board">
              <BoardPage />
            </ProtectedRoute>
          } />
          <Route path="/board/:boardId/write" element={
            <ProtectedRoute menuId="board">
              <BoardPage />
            </ProtectedRoute>
          } />

          {/* 뉴스 알림 - 권한 보호 */}
          <Route path="/news-alert" element={
            <ProtectedRoute menuId="news-alert">
              <NewsAlertPage />
            </ProtectedRoute>
          } />

          {/* 스터디 - 권한 보호 */}
          <Route path="/studies" element={
            <ProtectedRoute menuId="study">
              <StudyFeedPage />
            </ProtectedRoute>
          } />
          <Route path="/studies/new" element={
            <ProtectedRoute menuId="study">
              <StudyFormPage />
            </ProtectedRoute>
          } />
          <Route path="/studies/:id" element={
            <ProtectedRoute menuId="study">
              <StudyDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/studies/:id/edit" element={
            <ProtectedRoute menuId="study">
              <StudyFormPage />
            </ProtectedRoute>
          } />
          <Route path="/studies/:studyId/entries/new" element={
            <ProtectedRoute menuId="study">
              <StudyEntryFormPage />
            </ProtectedRoute>
          } />
          <Route path="/studies/:studyId/entries/:entryId/edit" element={
            <ProtectedRoute menuId="study">
              <StudyEntryFormPage />
            </ProtectedRoute>
          } />

          {/* 템플릿 - 권한 보호 */}
          <Route path="/templates" element={
            <ProtectedRoute menuId="study">
              <TemplateListPage />
            </ProtectedRoute>
          } />
          <Route path="/templates/new" element={
            <ProtectedRoute menuId="study">
              <TemplateFormPage />
            </ProtectedRoute>
          } />
          <Route path="/templates/:id/edit" element={
            <ProtectedRoute menuId="study">
              <TemplateFormPage />
            </ProtectedRoute>
          } />

          {/* 크롤링 관리 (관리자 전용) */}
          <Route path="/crawling" element={<AdminRoute><CrawlingDashboard /></AdminRoute>} />
          <Route path="/crawling/new" element={<AdminRoute><CrawlingRequestForm /></AdminRoute>} />
          <Route path="/crawling/edit/:id" element={<AdminRoute><CrawlingRequestForm /></AdminRoute>} />

          {/* 사용자 페이지 */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />

          {/* 관리자 페이지 */}
          <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
          <Route path="/menu-management" element={<AdminRoute><MenuManagement /></AdminRoute>} />
          <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />

          {/* 404 페이지 - board로 리다이렉트 */}
          <Route path="*" element={<Navigate to="/board" replace />} />
        </Routes>
      </div>
  );
}

// 최상위 App 컴포넌트
function App() {
  return (
    <Router>
      <ServerConfigProvider>
        <AuthProvider>
          <JWTAuthProvider>
            <FavoriteProvider>
              <MainApp />
            </FavoriteProvider>
          </JWTAuthProvider>
        </AuthProvider>
      </ServerConfigProvider>
    </Router>
  );
}

export default App;